import { useMsal } from "@azure/msal-react";
import {
  AccountInfo,
  BrowserAuthError,
  BrowserAuthErrorMessage,
  IPublicClientApplication,
} from "@azure/msal-browser";
import { loginRequest } from "../../AuthConfig";
import { DataError } from "../../core/errors/DataError";
import { NoCurrentAccountError } from "../../core/errors/NoCurrentAccountError";
import { mapToErrorStatus } from "../models/mappers/mapError";

interface AuthorizedFetch {
  authFetch(input: RequestInfo | URL, init?: RequestInit): Promise<Response>;
}

// B2C can hang for 90+ seconds (observed 04/07-2026); MSAL's silent renewal has
// no timeout of its own, so one hanging renewal froze every API call in the tab
// until the user reloaded. Bound the wait and fall back to a full redirect (an
// automatic "F5") instead.
const SILENT_TOKEN_TIMEOUT_MS = 12000;

class SilentTokenTimeoutError extends Error {
  constructor() {
    super(`acquireTokenSilent timed out after ${SILENT_TOKEN_TIMEOUT_MS} ms`);
    this.name = "SilentTokenTimeoutError";
  }
}

// Module scope: shared by every hook instance so at most ONE redirect is
// started no matter how many parallel API calls hit the timeout together
// (MSAL memoizes identical silent requests, so they all hang and expire
// almost simultaneously).
let tokenRedirectStarted = false;

type TokenRequest = typeof loginRequest & { account: AccountInfo };

function withTimeout<T>(promise: Promise<T>, ms: number): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    const timer = setTimeout(() => reject(new SilentTokenTimeoutError()), ms);
    promise.then(
      (value) => {
        clearTimeout(timer);
        resolve(value);
      },
      (error) => {
        clearTimeout(timer);
        reject(error);
      }
    );
  });
}

function isInteractionInProgressError(e: unknown): boolean {
  return (
    e instanceof BrowserAuthError &&
    e.errorCode === BrowserAuthErrorMessage.interactionInProgress.code
  );
}

// Deliberately never settles: the page is about to navigate away (full
// redirect = the automatic "F5"), so pending API calls must not proceed
// without a token nor flash error UI while navigation completes.
function never(): Promise<never> {
  return new Promise<never>(() => {});
}

function redirectForToken(
  instance: IPublicClientApplication,
  request: TokenRequest
): Promise<never> {
  if (!tokenRedirectStarted) {
    tokenRedirectStarted = true;
    // Reuse the existing deep-link restore (read by PreLoader in App.tsx) so
    // the user lands back on the page they were on after the round-trip.
    sessionStorage.setItem(
      "redirectOnLogin",
      window.location.pathname + window.location.search
    );
    instance.acquireTokenRedirect(request).catch((e) => {
      // Another interaction already owns the redirect - that one will heal us.
      if (!isInteractionInProgressError(e)) {
        tokenRedirectStarted = false; // allow a fresh attempt later
      }
    });
  }
  return never();
}

async function acquireAccessToken(
  instance: IPublicClientApplication,
  request: TokenRequest
): Promise<string> {
  try {
    const result = await withTimeout(
      instance.acquireTokenSilent(request),
      SILENT_TOKEN_TIMEOUT_MS
    );
    if (result.accessToken) return result.accessToken;
    // Silent succeeded but without a usable token: force a fresh login.
    return redirectForToken(instance, request);
  } catch (error) {
    if (isInteractionInProgressError(error)) {
      return never();
    }
    // Timeout, InteractionRequiredAuthError or any other silent failure.
    // NOTE: retrying silent here is pointless - MSAL memoizes identical
    // requests and would hand back the same hanging promise.
    return redirectForToken(instance, request);
  }
}

export function useAuthorizedFetch(): AuthorizedFetch {
  const { instance, accounts } = useMsal();

  async function authFetch(
    input: RequestInfo | URL,
    init?: RequestInit
  ): Promise<Response> {
    const currentAcc = accounts[0];

    if (!currentAcc) {
      throw new NoCurrentAccountError(
        "Cannot authorize request if no account is present"
      );
    }

    const request: TokenRequest = {
      ...loginRequest,
      account: currentAcc,
    };

    const accessToken = await acquireAccessToken(instance, request);

    let requestInit: RequestInit = {
      headers: {
        Authorization: `Bearer ${accessToken}`,
      },
    };

    if (init !== undefined) {
      requestInit = { ...requestInit, ...init };
    }

    const response = await fetch(input, requestInit);

    // If bad response throw DataError containing custom error code from backend if any
    if (!response.ok) {
      const errorStatus = mapToErrorStatus(await response.json());
      throw new DataError(errorStatus.message, errorStatus);
    }

    return response;
  }

  return {
    authFetch,
  };
}
